import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import AppButton from "../../components/form/AppButton";
import AppTextField from "../../components/form/AppTextField";
import { AuthPageShell } from "../../components/layout";
import authService from "../../services/authService";
import sessionAuthApi from "../../services/sessionAuth";
import { extractRolesFromUser, postLoginPath } from "../../utils/authorization";

/** Shown once after Google sign-up when the account has no workspace yet. */
export default function OnboardingWorkspaceUI() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    authService.getUser().then((user) => {
      if (!user) navigate("/login", { replace: true });
    });
  }, [navigate]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2 || trimmed.length > 120) {
      setError("Workspace name must be between 2 and 120 characters.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await sessionAuthApi.completeWorkspaceOnboarding(trimmed);
      const user = await authService.getUser();
      navigate(postLoginPath(extractRolesFromUser(user)), { replace: true });
    } catch (err: any) {
      setError(err?.message || "Could not create the workspace. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell
      maxWidth={480}
      title="Name your workspace"
      subtitle="Your workspace holds your data sources, reports and team members."
      footer={<Link to="/logout">Sign out</Link>}
    >
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <AppTextField
            label="Workspace name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
          <AppButton type="submit" fullWidth variant="contained" sx={{ mt: 1.5 }} loading={loading}>
            Create workspace
          </AppButton>
        </Stack>
      </Box>
    </AuthPageShell>
  );
}
